import { Button } from "@lumi/ui-headless";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../shared/components/ui/cn";
import { useCascadeFadeIn } from "../../shared/hooks/useCascadeFadeIn";
import { useTransitionNavigation } from "../../shared/hooks/useTransitionNavigation";
import { pageTransitions } from "../../shared/transitions/pageTransitions";
import { useTitles } from "./hooks/useTitles";
import { LoadingSpinner } from "../../shared/components/loading/LoadingSpinner";

/**
 * Titles Library Page - Admin Title Management
 * Lists all published titles with search and edit functionality
 */
/* eslint-disable max-lines-per-function */

/**
 * Individual animated title card wrapper
 */
function AnimatedTitleCard({
  title,
  index,
  onEdit,
}: {
  readonly title: {
    id: string;
    name: string;
    year?: number;
    directorName?: string;
    posterUrlHd?: string;
  };
  readonly index: number;
  readonly onEdit: (id: string) => void;
}) {
  const { ref, isVisible } = useCascadeFadeIn({
    index,
    threshold: 0.1,
    staggerDelay: 50,
  });

  return (
    <div
      ref={ref}
      className={cn(
        "group relative bg-transparent border border-neutral-800 rounded-lg overflow-hidden hover:border hover:border-lumi-accent transition-all flex flex-col duration-500 ease-in-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
      )}
    >
      {/* Poster Image */}
      <div className="aspect-[2/3] bg-neutral-800">
        {title.posterUrlHd ? (
          <img src={title.posterUrlHd} alt={title.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-600">No Image</div>
        )}
      </div>

      {/* Title Info */}
      <div className="p-3 flex flex-col flex-1">
        <h3 className="text-white text-sm font-medium line-clamp-2 mb-1">{title.name}</h3>
        <p className="text-neutral-500 text-xs mb-2">
          {title.year || "N/A"}
          {title.directorName && ` • ${title.directorName}`}
        </p>

        {/* Edit Button - pushed to bottom with mt-auto */}
        <button
          onClick={() => onEdit(title.id)}
          className="w-full px-3 py-1.5 bg-neutral-800/0 hover:text-lumi-accent text-white text-xs font-medium rounded transition-colors mt-auto"
        >
          Edit
        </button>
      </div>
    </div>
  );
}

export function TitlesLibrary() {
  const navigate = useNavigate();
  const { transitionTo } = useTransitionNavigation();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 24;

  // Fetch titles with current filters
  const { titles, pagination, loading, error } = useTitles(searchQuery, currentPage, pageSize);

  // Handle search submit
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  // Handle edit click with view transition
  const handleEdit = (titleId: string) => {
    transitionTo(`/admin/titles/${titleId}/edit`, pageTransitions.slideHorizontal);
  };

  return (
    <div className="mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Title Library</h1>
            <p className="text-sm text-neutral-400 pb-6">Browse and edit published titles in the catalog</p>
          </div>
          <Button variant="ghost-sm" onClick={() => navigate("/admin")}>
            ← Back to Dashboard
          </Button>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by title, director, or genre..."
              className="w-full px-4 py-2 bg-transparent border border-neutral-800 rounded text-white placeholder-neutral-500 focus:outline-none focus:border-lumi-accent"
            />
            {searchInput && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
              >
                ✕
              </button>
            )}
          </div>
          <Button type="submit" variant="outline">
            Search
          </Button>
        </form>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Results Summary */}
      {!loading && pagination && (
        <div className="mb-4">
          <p className="text-neutral-400 text-sm">
            Showing {titles.length} of {pagination.total} titles
            {searchQuery && ` matching "${searchQuery}"`}
          </p>
        </div>
      )}

      {/* Titles Grid */}
      {!loading && titles.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          {titles.map((title, index) => (
            <AnimatedTitleCard key={title.id} title={title} index={index} onEdit={handleEdit} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && titles.length === 0 && !error && (
        <div className="text-center py-12">
          <p className="text-neutral-400 text-lg mb-2">No titles found</p>
          {searchQuery && (
            <button onClick={handleClearSearch} className="text-lumi-accent hover:underline text-sm">
              Clear search
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            variant="ghost"
          >
            Previous
          </Button>

          <span className="text-neutral-400 px-4">
            Page {currentPage} of {pagination.totalPages}
          </span>

          <Button
            onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={currentPage === pagination.totalPages}
            variant="ghost"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
